import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", phone: "",
    address: "", city: "", postcode: ""
  });

  const shipping_fee = subtotal >= 100 ? 0 : (items.length ? 9.90 : 0);
  const total = subtotal + shipping_fee;

  const onChange = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const placeOrder = async (e) => {
    e.preventDefault();
    setErr("");
    if (items.length === 0) { setErr("Cart is empty"); return; }
    for (const k of ["name", "email", "address", "city", "postcode"]) {
      if (!form[k].trim()) { setErr("Please fill in all shipping fields."); return; }
    }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        shipping: form,
        customer_email: form.email,
      };
      const r = await api.post("/checkout/session", payload);
      // keep cart until success page confirms paid; clear on redirect
      localStorage.setItem("ks_last_order", r.data.order_id);
      window.location.href = r.data.url;
    } catch (e) {
      setErr(e.response?.data?.detail || "Checkout failed. Try again.");
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="p-16 text-center">
        <div className="text-3xl font-black uppercase tracking-tighter font-[Outfit]">Your Cart Is Empty</div>
        <p className="mt-2 text-sm text-zinc-600">Add something before checking out.</p>
        <button onClick={() => nav("/products")} className="btn-brutal btn-red mt-6">Browse Products</button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 border-b border-zinc-900">
      <form onSubmit={placeOrder} className="lg:col-span-3 p-6 md:p-12 border-r border-zinc-900">
        <div className="tag mb-4">Checkout</div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter uppercase font-[Outfit]">Shipping Details</h1>
        <p className="text-sm text-zinc-600 mt-2">Guest checkout is supported. We use Stripe for secure payment.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest font-bold font-[Outfit] block mb-2">Full Name *</label>
            <input data-testid="checkout-name" required className="input-brutal" value={form.name} onChange={onChange("name")} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold font-[Outfit] block mb-2">Email *</label>
            <input data-testid="checkout-email" type="email" required className="input-brutal" value={form.email} onChange={onChange("email")} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold font-[Outfit] block mb-2">Phone</label>
            <input data-testid="checkout-phone" className="input-brutal" value={form.phone} onChange={onChange("phone")} />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest font-bold font-[Outfit] block mb-2">Street Address *</label>
            <input data-testid="checkout-address" required className="input-brutal" value={form.address} onChange={onChange("address")} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold font-[Outfit] block mb-2">City *</label>
            <input data-testid="checkout-city" required className="input-brutal" value={form.city} onChange={onChange("city")} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold font-[Outfit] block mb-2">Postcode *</label>
            <input data-testid="checkout-postcode" required className="input-brutal" value={form.postcode} onChange={onChange("postcode")} />
          </div>
        </div>

        {err && <div data-testid="checkout-error" className="mt-6 border border-[#ff3333] bg-[#fff3f3] px-4 py-3 text-sm text-[#cc0000] uppercase tracking-wider font-bold font-[Outfit]">{err}</div>}

        <button data-testid="checkout-submit" type="submit" disabled={submitting} className="btn-brutal btn-red mt-8 w-full md:w-auto">
          {submitting ? "Redirecting To Stripe…" : `Pay $${total.toFixed(2)} NZD →`}
        </button>
      </form>

      <aside className="lg:col-span-2 bg-zinc-100 p-6 md:p-10">
        <div className="text-xs uppercase tracking-widest font-bold font-[Outfit] mb-4">Order Summary</div>
        <div className="border border-zinc-900 bg-white divide-y divide-zinc-200">
          {items.map(i => (
            <div key={i.product_id} className="flex gap-3 p-3">
              <div className="w-14 h-14 bg-zinc-100 border border-zinc-900 flex-shrink-0">
                {i.image && <img src={i.image} alt="" className="w-full h-full object-contain p-1" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold line-clamp-2 leading-tight">{i.title}</div>
                <div className="text-xs text-zinc-600 mt-1">Qty {i.quantity} · ${i.price.toFixed(2)}</div>
              </div>
              <div className="font-bold font-[Outfit]">${(i.price * i.quantity).toFixed(2)}</div>
            </div>
          ))}
        </div>
        <dl className="mt-6 space-y-2 text-sm">
          <div className="flex justify-between"><dt>Subtotal</dt><dd data-testid="summary-subtotal">${subtotal.toFixed(2)}</dd></div>
          <div className="flex justify-between"><dt>Shipping</dt><dd>{shipping_fee === 0 ? "FREE" : `$${shipping_fee.toFixed(2)}`}</dd></div>
          <div className="flex justify-between border-t border-zinc-900 pt-3 text-lg font-black font-[Outfit]">
            <dt>Total NZD</dt><dd data-testid="summary-total">${total.toFixed(2)}</dd>
          </div>
        </dl>
      </aside>
    </div>
  );
}
